/**
 * ゲスト用のストア（localStorage）。未ログインでもしおり・再開位置が端末に残る。
 *
 * 保存は 1 キーの JSON 1 本（キー名は brand.ts の STORAGE_KEY）。localStorage が使えない環境
 * （プライベートモード・容量超過・無効化）ではメモリ上のスナップショットに縮退し、
 * 例外を投げない — 読書を止めないため。
 */
import { STORAGE_KEY } from '../brand';
import { newerProgress, removeItem, upsertItem } from './merge';
import type { ListType, LocalSnapshot, MarkType, UserDataStore, UserItem } from './store';
import { emptySnapshot, nowSec } from './store';

/** localStorage を読み書きする最小ラッパ（失敗は握る）。 */
export class LocalUserData implements UserDataStore {
  private snapshot: LocalSnapshot;

  constructor(private readonly storage: Storage | null) {
    this.snapshot = this.read();
  }

  list(type: ListType): Promise<UserItem[]> {
    if (type === 'history') {
      // 履歴＝再開位置を最終閲覧の新しい順に並べたもの（サーバ側と同じ意味論）
      const items = Object.values(this.snapshot.progress)
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((entry) => ({ book: entry.book, page: entry.page, updatedAt: entry.updatedAt }));
      return Promise.resolve(items);
    }
    return Promise.resolve([...this.snapshot[type]]);
  }

  put(type: MarkType, item: UserItem): Promise<void> {
    this.snapshot = { ...this.snapshot, [type]: upsertItem(this.snapshot[type], item) };
    this.write();
    return Promise.resolve();
  }

  remove(type: MarkType, book: string, page?: number): Promise<void> {
    this.snapshot = { ...this.snapshot, [type]: removeItem(this.snapshot[type], book, page) };
    this.write();
    return Promise.resolve();
  }

  progress(book: string): Promise<number | null> {
    return Promise.resolve(this.snapshot.progress[book]?.page ?? null);
  }

  saveProgress(book: string, page: number, updatedAt: number = nowSec()): Promise<void> {
    const entry = newerProgress(this.snapshot.progress[book], { book, page, updatedAt });
    this.snapshot = { ...this.snapshot, progress: { ...this.snapshot.progress, [book]: entry } };
    this.write();
    return Promise.resolve();
  }

  /** マージ判定に使う現在のスナップショット。 */
  current(): LocalSnapshot {
    return this.snapshot;
  }

  /** マージ済みの印など、スナップショットを差し替える。 */
  replace(snapshot: LocalSnapshot): void {
    this.snapshot = snapshot;
    this.write();
  }

  private read(): LocalSnapshot {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return emptySnapshot();
      const parsed = JSON.parse(raw) as Partial<LocalSnapshot>;
      return {
        ...emptySnapshot(),
        ...parsed,
        // 型が壊れた保存内容でも起動を妨げない
        bookmark: Array.isArray(parsed.bookmark) ? parsed.bookmark : [],
        sticky: Array.isArray(parsed.sticky) ? parsed.sticky : [],
        shelf: Array.isArray(parsed.shelf) ? parsed.shelf : [],
        progress: typeof parsed.progress === 'object' && parsed.progress ? parsed.progress : {},
      };
    } catch {
      return emptySnapshot();
    }
  }

  private write(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.snapshot));
    } catch {
      // 容量超過・無効化。メモリ上の状態だけで続ける。
    }
  }
}

/** localStorage を安全に取り出す（アクセス自体が例外になる環境がある）。 */
export function safeStorage(view: Window | null): Storage | null {
  try {
    return view?.localStorage ?? null;
  } catch {
    return null;
  }
}
